import { useMemo } from 'react';
import type { TrackingEvent } from '../../../utils/analytics';

interface TopSectionsProps {
  events: TrackingEvent[];
}

const SECTION_LABELS: Record<string, string> = {
  Prodotti:    'Prodotti',
  Tecnica:     'Tecnica',
  Preventivo:  'Preventivo',
  Home:        'Home',
};

const SECTION_ICONS: Record<string, string> = {
  Prodotti:    'ri-box-3-line',
  Tecnica:     'ri-settings-3-line',
  Preventivo:  'ri-file-list-3-line',
  Home:        'ri-home-5-line',
};

export default function TopSections({ events }: TopSectionsProps) {
  const navEvents = useMemo(() => events.filter((e) => e.type === 'nav_click'), [events]);

  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    navEvents.forEach((e) => {
      map[e.label] = (map[e.label] ?? 0) + 1;
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [navEvents]);

  const max = counts[0]?.[1] ?? 1;
  const total = navEvents.length;

  if (total === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-[#E5E5E5] p-5 md:p-7">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-black text-[#000000]" style={{ fontFamily: "'Raleway', sans-serif" }}>
            Sezioni più visitate
          </h3>
          <p className="text-[10px] text-[#A5A5A5] mt-0.5">{total} click di navigazione totali</p>
        </div>
        <div className="w-9 h-9 flex items-center justify-center bg-[#555555]/10 rounded-xl">
          <i className="ri-compass-3-line text-[#555555] text-lg" />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {counts.map(([label, count]) => {
          const pct = Math.round((count / max) * 100);
          const totalPct = Math.round((count / total) * 100);
          const icon = SECTION_ICONS[label] ?? 'ri-link';
          const name = SECTION_LABELS[label] ?? label;
          return (
            <div key={label} className="flex items-center gap-3">
              <div className="w-7 h-7 flex items-center justify-center bg-[#F6F6F6] rounded-lg flex-shrink-0">
                <i className={`${icon} text-[#555555] text-sm`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-[#000000]">{name}</span>
                  <span className="text-xs text-[#A5A5A5] flex-shrink-0 ml-2">
                    {count} click · <span className="text-[#555555] font-semibold">{totalPct}%</span>
                  </span>
                </div>
                <div className="h-1.5 bg-[#F6F6F6] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#555555] rounded-full transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
